<template>
    <div class="compare-wells">
        <div class="compare-well-selector">
            <div
                v-for="well in existingWells"
                :key="well && well._id"
                v-b-tooltip.hover
                class="compare-well-small-card"
                :class="{
                    selected: isSelected(well),
                    focused: hoveredWellId === well._id,
                    disable_add_remove: isPlaying
                }"
                :title="isPlaying ? $translate('stop_playback_to_add_remove') : ''"
                @mouseenter="setHoveredWellId(well)"
                @mouseleave="resetHoveredWellId"
                @click="selectForComparison(well)"
            >
                <well-preview-image class="mx-1" :well="well" :hide-well-info="true" />

                <div class="well-card-title" :class="{ 'show-as-column': isGeriOrMiri }">
                    <well-chamber-number v-if="isGeriOrMiri" />
                    <well-id :well="well" />
                    <well-embryo-id-view :well="well" />
                </div>

                <well-score-metrics
                    v-if="!isFdaFriendly"
                    :well="well"
                    size="compare-well-small-card"
                />
            </div>
        </div>

        <wells-sub-header />

        <div class="compare-wells-selected-container">
            <compare-well-component
                v-for="(well, index) in selectedWellsForComparison"
                :key="well._id"
                :well="well"
                :index="index"
            />
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { INSTRUMENT_TYPE } from '../../business/const'
import WellsSubHeader from './WellsSubHeader'
import WellPreviewImage from './WellPreviewImage'
import WellScoreMetrics from './common/WellScoreMetrics'
import CompareWellComponent from './CompareWellComponent'
import KeyboardService from '../../business/services/KeyboardService'
import WellId from './WellId'
import WellEmbryoIdView from './common/WellEmbryoIdView'
import WellChamberNumber from './WellChamberNumber'

export default {
    name: 'CompareWells',
    components: {
        WellChamberNumber,
        WellEmbryoIdView,
        WellId,
        CompareWellComponent,
        WellScoreMetrics,
        WellPreviewImage,
        WellsSubHeader
    },
    computed: {
        ...mapState({
            hoveredWellId: state => state.timelapse.hoveredWellId,
            treatment: state => state.timelapse.selectedTreatment,
            selectedSlide: state => state.timelapse.selectedSlide,
            selectedForComparison: state => state.timelapse.selectedForComparison,
            videoPlaybackReady: state => state.timelapse.videoPlaybackReady,
            playState: state => state.video.playState,
            currentView: state => state.navigation.currentView
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            availableMKEvents: 'video/availableMKEvents',
            getSelectedInstrument: 'timelapse/getSelectedInstrument'
        }),
        existingWells() {
            return this.treatment.wellsByIndex.filter(well => well)
        },
        isPlaying() {
            return this.playState === 'play'
        },
        isGeriOrMiri() {
            return (
                this.getSelectedInstrument.type &&
                [INSTRUMENT_TYPE.GERI, INSTRUMENT_TYPE.MIRI].includes(
                    this.getSelectedInstrument.type
                )
            )
        },
        selectedWellsForComparison() {
            const { wellsByIndex } = this.treatment
            return wellsByIndex.filter(well => this.selectedForComparison.includes(well._id))
        }
    },
    mounted() {
        KeyboardService.subscribeTo(
            'wellCards',
            'Space',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/togglePlayStop')
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'ArrowLeft',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: false })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'ArrowRight',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: true })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'KeyA',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: false })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'KeyD',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: true })
                }
            },
            true
        )
    },
    beforeDestroy() {
        KeyboardService.unsubscribeAll('wellCards', 'Space')
        KeyboardService.unsubscribeAll('wellCards', 'ArrowLeft')
        KeyboardService.unsubscribeAll('wellCards', 'ArrowRight')
        KeyboardService.unsubscribeAll('wellCards', 'KeyA')
        KeyboardService.unsubscribeAll('wellCards', 'KeyD')
    },
    methods: {
        setHoveredWellId(well) {
            this.$store.state.timelapse.hoveredWellId = well._id
        },
        resetHoveredWellId() {
            this.$store.state.timelapse.hoveredWellId = null
        },
        isSelected(well) {
            return this.selectedForComparison.includes(well._id)
        },
        selectForComparison(well) {
            if (this.isPlaying) return

            this.$store.dispatch('timelapse/addRemoveFromWellComparison', {
                wellId: well._id,
                add: !this.isSelected(well)
            })

            this.$store.dispatch('video/skipToFrame', { frame: 0 })
            this.$store.commit('video/selectedMKEventSN', this.availableMKEvents[0])
        }
    }
}
</script>

<style lang="scss" scoped>
.compare-wells {
    width: 100%;
    max-height: 100%;
    display: grid;
    grid-template-rows: repeat(2, min-content) 1fr;

    .compare-well-selector {
        padding: 0.5rem;
        margin-bottom: 0.5rem;
        display: flex;
        overflow-x: auto;
        @include hide-scrollbar();
        background-color: $dark-2;

        .compare-well-small-card {
            position: relative;
            transition: 0.2s all;
            cursor: pointer;
            @include prevent-select();

            &.disable_add_remove {
                opacity: 0.3 !important;
            }

            .well-card-title {
                width: 90%;
                padding-left: 0.5rem;
                position: absolute;
                left: 0;
                top: 0.5rem;
                display: flex;
                color: $light-3;
                font-size: 8px;
                overflow: hidden;

                &:not(.show-as-column) {
                    justify-content: space-between;
                }

                &.show-as-column {
                    flex-direction: column;
                    align-items: start;
                }
            }

            .well-score-metrics {
                position: absolute;
                bottom: 0.25rem;
                right: 0.5rem;
                pointer-events: none;
            }
        }
    }

    .wells-sub-header {
        margin: 0.25rem 0 0.5rem;
    }

    .compare-wells-selected-container {
        width: 100%;
        padding: 0 0.5rem;
        display: flex;
        overflow: scroll;

        .compare-well-component {
            padding-bottom: 0.5rem;
        }
    }
}
</style>

<style lang="scss">
.compare-wells {
    .score-hazard-icon img {
        height: 1rem;
    }

    .compare-well-small-card {
        .well-embryo-id-view {
            font-size: 6pt;
        }
        .well-score-metrics {
            .teardrop {
                padding: 0.1rem 0.3rem !important;
                background: $dark-3;
            }
        }

        .well-status-overlay {
            transition: 0.2s all;
            opacity: 0.3;
        }

        .well-preview {
            transition: 0.2s all;
            opacity: 0.2;
            border: $border-narrow-dark;
            width: 5.5rem;
            height: 5.5rem;
            box-shadow: 0 0 10px 0 $shadow !important;
        }

        &.focused {
            z-index: 3;
            transform: scale(1.15);

            .well-preview {
                opacity: 1;
            }
        }

        &.selected {
            .well-status-overlay {
                opacity: 0.9;
                border: 0.1rem solid $light-2;
            }

            .well-preview {
                opacity: 1;
            }
        }
    }

    .morphokinetics-table {
        .morphokinetics-table-header {
            font-size: 10pt;
            background-color: $dark-2;
            margin-bottom: 0.5rem;
        }
    }

    .annotation-hazard {
        width: 3rem;
        padding: 0 0.2rem 0 0 !important;
        margin-left: -0.4rem;

        .hazard-icon {
            height: 1rem;
        }
    }

    .annotations-message {
        margin: 0;
        border: none;
        background-color: transparent;
    }
}
</style>
